import { css } from "@emotion/react";
import { SwarovskiOptikLocaleFrontend, type TranslatedValue } from "@swo/storefront";
import { ContentContainer } from "../../components/_shared-components/arrangement/Container";
import { Spacer } from "../../components/_shared-components/arrangement/Space";
import { ContentfulImage2 } from "../../components/_shared-components/image-related/ContentfulImage2";
import { SwarovskiLink } from "../../components/_shared-components/ui-elements/SwarovskiLink";
import { FONTS, fontSize } from "../../styles/fonts";
import { CSS_COLORS, CSS_SPACER_SIZES } from "../../styles/styles-css-variables";
import { head, ReactTFC } from "../../utils/fp/missing-ts-parts";
import { PDPHeadlineOnBackground } from "./pdp-headline";

import { IconPlus } from "../../assets";
import { formatOptionalStartingPrice } from "../../domain/swarovski-optik/products/price/format-price";
import { useGeneralTranslationsContext } from "../../global/hooks/use-general-translations-context";
import { CSS_MQ } from "../../styles/breakpoints";
import { getImages } from "./getImages";
import { PDPSwarovskiProductFrontend, PDPSwarovskiProductVariantFrontend } from "./PDP_SingleImageCarousel/state/types";
import { T } from "../../components/T";

type PDPProductComparisonProps = {
  link: {
    pathname: string;
    search: string | undefined;
  };
  label: TranslatedValue;
  selectedProduct: PDPSwarovskiProductFrontend;
  selectedVariant: PDPSwarovskiProductVariantFrontend;
  locale: SwarovskiOptikLocaleFrontend;
  techSpecsAttributes: Array<{
    label: string;
    valueLabel: string;
  }>;
};

export const PdpProductComparisonComponent: ReactTFC<PDPProductComparisonProps> = ({
  link,
  label,
  selectedProduct,
  selectedVariant,
  locale,
  techSpecsAttributes,
}) => {
  const image = getImage(selectedProduct, selectedVariant);

  const fromPriceLabel = useGeneralTranslationsContext()("fromPriceLabel");
  const lowestPriceLast30DaysTranslation = useGeneralTranslationsContext()("lowestPriceLast30Days");

  return (
    <>
      <Spacer size={"md"} />

      <div
        css={css`
          background-color: var(--so-green-white);
          padding: ${CSS_SPACER_SIZES.md} 0px;
        `}
      >
        <ContentContainer>
          <PDPHeadlineOnBackground>
            <T>{label}</T>
          </PDPHeadlineOnBackground>

          <div
            css={css`
              display: grid;
              grid-template-columns: minmax(0, 5fr) minmax(0, 4fr);
              ${CSS_MQ.atLeastSm} {
                grid-template-columns: repeat(2, minmax(0, 1fr));
              }
              margin: 0 auto;

              width: 100%;
              gap: 12px;
              ${CSS_MQ.atLeastSm} {
                width: 100%;
                gap: 40px;
              }
              ${CSS_MQ.atLeastMd} {
                width: 600px;
              }
              ${CSS_MQ.atLeastXl} {
                width: 700px;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;

                gap: 20px;
                padding: 0;
                ${CSS_MQ.atLeastSm} {
                  gap: 30px;
                  padding: 20px 0;
                }
              `}
            >
              {image && (
                <ContentfulImage2
                  assetId={image.sys.id}
                  contentfulUrl={image.url}
                  withTransparency={true}
                  alt={image.description || undefined}
                  css={css`
                    width: 100%;
                    max-height: 100px;
                    ${CSS_MQ.atLeastXl} {
                      max-height: 140px;
                    }
                    object-fit: contain;
                  `}
                  sizes="(max-width: 575px) 200px, 300px"
                  cropping={undefined}
                />
              )}

              <span css={[FONTS.SwarovskiFutura500, fontSize.x2]} className="u-ta-center u-text-balance">
                {selectedProduct.name}
              </span>

              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 12px;
                `}
              >
                {techSpecsAttributes.map(spec => {
                  return (
                    <div key={JSON.stringify(spec)} css={FONTS.SwarovskiFutura300} className={"u-ta-center"}>
                      <span className={"u-d-block"} css={fontSize.x25}>
                        {spec.valueLabel}
                      </span>
                      <span css={[fontSize.x2s]}>{spec.label}</span>
                    </div>
                  );
                })}
              </div>

              <span css={[fontSize.x2, FONTS.SwarovskiFutura300]}>
                <T>
                  {formatOptionalStartingPrice(
                    selectedProduct.startingPrice
                      ? {
                          startingPrice: selectedProduct.startingPrice,
                          discountedStartingPrice: selectedProduct.discountedStartingPrice,
                          lowestPrice30Days: selectedProduct.lowestPrice30Days,
                          discountPercentage: selectedProduct.discountPercentage,
                        }
                      : undefined,
                    fromPriceLabel,
                    lowestPriceLast30DaysTranslation,
                    locale,
                  )}
                </T>
              </span>
            </div>

            <SwarovskiLink
              isInternal={true}
              link={link}
              scrollToTop={true}
              styled={"no-decoration"}
              attachStyles={[
                FONTS.SwarovskiFutura500,
                fontSize.x2,
                css`
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  flex-direction: column;
                  text-align: center;

                  color: black;

                  padding: 20px;

                  border-radius: 8px;
                  border-width: 1px;
                  border-style: solid;

                  border-color: ${CSS_COLORS.Grey5};
                  box-shadow: 0 0 20px 4px #00000020;

                  transition:
                    background-color 0.3s ease,
                    box-shadow 0.3s ease;

                  &:hover {
                    background-color: ${CSS_COLORS.Grey0};

                    box-shadow: 0 0 20px 4px #00000030;
                  }

                  height: 100%;
                  flex-grow: 1;

                  text-transform: uppercase;
                `,
              ]}
            >
              <IconPlus
                css={css`
                  width: 80px;
                  height: 80px;
                `}
              />
              <span>
                <T>{label}</T>
              </span>
            </SwarovskiLink>
          </div>
        </ContentContainer>
      </div>
    </>
  );
};

function getImage(selectedProduct: PDPSwarovskiProductFrontend, selectedVariant: PDPSwarovskiProductVariantFrontend) {
  const productImages = getImages(selectedProduct.images);
  const variantImages = getImages(selectedVariant.images);

  const images = variantImages.length > 0 ? variantImages : productImages;

  return head(images);
}
